import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'

// Types for inventory API
export interface StockInfo {
  variant_id: string
  product_id: string
  product_name: string
  variant_name: string
  variant_sku: string
  current_stock: number
  reserved_stock: number
  available_stock: number
  minimum_stock: number
  is_low_stock: boolean
  is_set_selling: boolean
  set_size: number
  location?: string
  last_updated: string
}

export interface InventorySummary {
  total_variants: number
  total_stock_quantity: number
  total_stock_value: number
  inventory_turnover_ratio: number
  low_stock_variants: number
  out_of_stock_variants: number
  overstocked_variants: number
  categories_summary: Array<{
    category_id: string
    category_name: string
    variant_count: number
    total_stock: number
    stock_value: number
  }>
}

export interface InventoryDashboardData {
  summary: InventorySummary
  inventory_value_trend: Array<{
    month: string
    value: number
  }>
  movement_velocity: {
    fast_moving: { count: number; percentage: number }
    medium_moving: { count: number; percentage: number }
    slow_moving: { count: number; percentage: number }
  }
  top_categories: Array<{
    category_name: string
    stock_value: number
    percentage: number
  }>
  top_brands: Array<{
    brand_name: string
    stock_value: number
    percentage: number
  }>
}

export interface LowStockVariant {
  variant_id: string
  product_id: string
  product_name: string
  variant_name: string
  variant_sku: string
  current_stock: number
  reserved_stock: number
  available_stock: number
  minimum_stock: number
  shortage_quantity: number
  stock_deficit: number
  days_until_stockout: number
  is_set_selling: boolean
  set_size: number
}

export interface BalancingRecommendation {
  variant_id: string
  variant_sku: string
  variant_name: string
  product_name: string
  category_name?: string
  brand_name?: string
  warehouse_name?: string
  current_stock: number
  reserved_stock: number
  available_stock: number
  minimum_stock: number
  reorder_point: number
  average_daily_sales_30d: number
  average_daily_sales_60d: number
  average_daily_sales_90d: number
  days_of_stock_remaining: number
  suggested_order_quantity: number
  sales_velocity: string
  trend_direction: string
  confidence_level: string
  last_restock_date?: string
  lead_time_days: number
  safety_stock_multiplier: number
}

export interface BalancingFilters {
  category_id?: string
  brand_id?: string
  warehouse_id?: string
  stock_status?: string
  sales_velocity?: string
  confidence_level?: string
}

export interface BalancingReportResponse {
  recommendations: BalancingRecommendation[]
  summary: {
    total_variants: number
    variants_needing_reorder: number
    total_suggested_order_value: number
    fast_moving_variants: number
    slow_moving_variants: number
  }
  filters_applied: BalancingFilters
  pagination: {
    limit: number
    offset: number
    total: number
    has_more: boolean
  }
}

export interface ReorderPointUpdate {
  variant_id: string
  new_reorder_point: number
  new_safety_stock_multiplier?: number
}

export interface BulkReorderPointUpdateResponse {
  successful_updates: number
  failed_updates: number
  errors: string[]
  total_processed: number
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  per_page: number
  pages: number
  has_more: boolean
}

export const inventoryApi = createApi({
  reducerPath: 'inventoryApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin/inventory`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Inventory', 'Stock', 'LowStock'],
  endpoints: (builder) => ({
    getStockInfo: builder.query<PaginatedResponse<StockInfo>, {
      page?: number
      per_page?: number
      search?: string
      category_id?: string
      low_stock_only?: boolean
    }>({
      query: (params) => ({
        url: '/stock',
        params,
      }),
      providesTags: ['Stock'],
    }),
    getInventorySummary: builder.query<InventorySummary, {
      category_id?: string
      product_id?: string
    }>({
      query: (params) => ({
        url: '/summary',
        params,
      }),
      providesTags: ['Inventory'],
    }),
    getInventoryDashboard: builder.query<InventoryDashboardData, void>({
      query: () => '/reports/summary-dashboard',
      providesTags: ['Inventory'],
    }),
    getLowStockVariants: builder.query<LowStockVariant[], {
      threshold_multiplier?: number
      limit?: number
    }>({
      query: (params) => ({
        url: '/low-stock',
        params,
      }),
      providesTags: ['LowStock'],
    }),
    adjustStock: builder.mutation<any, {
      variant_id: string
      adjustment_type: 'increase' | 'decrease' | 'set'
      quantity: number
      reason: string
      notes?: string
    }>({
      query: (data) => ({
        url: '/stock/adjust',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Stock', 'Inventory', 'LowStock'],
    }),
    exportStock: builder.mutation<Blob, {
      category_id?: string
      product_id?: string
    }>({
      query: (params) => ({
        url: '/export',
        method: 'GET',
        params,
        responseHandler: (response) => response.blob(),
      }),
    }),
    importStock: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/import',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Stock', 'Inventory', 'LowStock'],
    }),
    getBalancingRecommendations: builder.query<BalancingReportResponse, {
      filters?: BalancingFilters
      limit?: number
      offset?: number
    }>({
      query: ({ filters = {}, limit = 50, offset = 0 }) => {
        const params: any = { limit, offset }
        if (filters.category_id) params.category_id = filters.category_id
        if (filters.brand_id) params.brand_id = filters.brand_id
        if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id
        if (filters.stock_status && filters.stock_status !== 'all') params.stock_status = filters.stock_status
        if (filters.sales_velocity && filters.sales_velocity !== 'all') params.sales_velocity = filters.sales_velocity
        if (filters.confidence_level && filters.confidence_level !== 'all') params.confidence_level = filters.confidence_level

        return {
          url: '/balancing/recommendations',
          params,
        }
      },
      providesTags: ['Inventory'],
    }),
    updateReorderPoints: builder.mutation<BulkReorderPointUpdateResponse, {
      updates: ReorderPointUpdate[]
    }>({
      query: ({ updates }) => ({
        url: '/balancing/update-reorder-points',
        method: 'POST',
        body: { updates },
      }),
      invalidatesTags: ['Stock', 'Inventory'],
    }),
    exportBalancingReport: builder.mutation<Blob, {
      filters?: BalancingFilters
      format?: 'excel' | 'csv'
    }>({
      query: ({ filters = {}, format = 'excel' }) => {
        const params: any = { format }
        if (filters.category_id) params.category_id = filters.category_id
        if (filters.brand_id) params.brand_id = filters.brand_id
        if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id
        if (filters.stock_status && filters.stock_status !== 'all') params.stock_status = filters.stock_status

        return {
          url: '/balancing/export',
          method: 'GET',
          params,
          responseHandler: (response) => response.blob(),
        }
      },
    }),
    // Stock Adjustments
    getStockAdjustments: builder.query<any, {
      page?: number
      per_page?: number
      adjustment_type?: string
      status?: string
      warehouse_id?: string
      category_id?: string
      start_date?: string
      end_date?: string
    }>({
      query: (params) => ({
        url: '/adjustments',
        params,
      }),
      providesTags: ['Stock'],
    }),
    createStockAdjustment: builder.mutation<any, {
      variant_id: string
      warehouse_id?: string
      adjustment_type: string
      quantity: number
      reason: string
      evidence_urls?: string[]
    }>({
      query: (data) => ({
        url: '/adjustments',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Stock', 'Inventory'],
    }),
    approveStockAdjustment: builder.mutation<any, string>({
      query: (adjustmentId) => ({
        url: `/adjustments/${adjustmentId}/approve`,
        method: 'POST',
      }),
      invalidatesTags: ['Stock', 'Inventory'],
    }),
    getShrinkageReport: builder.query<any, {
      start_date?: string
      end_date?: string
    }>({
      query: (params) => ({
        url: '/adjustments/shrinkage-report',
        params,
      }),
    }),
    exportStockAdjustments: builder.mutation<Blob, {
      adjustment_type?: string
      status?: string
      start_date?: string
      end_date?: string
      format?: 'excel' | 'csv'
    }>({
      query: ({ format = 'excel', ...filters }) => {
        const params: any = { format, ...filters }
        return {
          url: '/adjustments/export',
          method: 'GET',
          params,
          responseHandler: (response) => response.blob(),
        }
      },
    }),
  }),
})

export const {
  useGetStockInfoQuery,
  useGetInventorySummaryQuery,
  useGetInventoryDashboardQuery,
  useGetLowStockVariantsQuery,
  useAdjustStockMutation,
  useExportStockMutation,
  useImportStockMutation,
  useGetBalancingRecommendationsQuery,
  useUpdateReorderPointsMutation,
  useExportBalancingReportMutation,
  // Stock Adjustments
  useGetStockAdjustmentsQuery,
  useCreateStockAdjustmentMutation,
  useApproveStockAdjustmentMutation,
  useGetShrinkageReportQuery,
  useExportStockAdjustmentsMutation,
} = inventoryApi